import { useNavigation, useRoute } from "@react-navigation/native";
import React, { memo, useCallback, useEffect, useState } from "react";
import { View } from "react-native";
import { useSelector } from "react-redux";
import LeaderReferLink from "@components/Profile/LeaderReferLink";
import BannerListHorizontal, {
  BannerListHorizontalItem,
} from "@screens/LeaderTraining/BannerListHorizontal";
import { VideoListingItem } from "@screens/LeaderTraining/TrainingVideoListing";
import StoryBanner from "@screens/Home/StoryBanner";
import CompleteClProfile from "@screens/Home/CompleteClProfile";
import SettingsCard from "@screens/Home/SettingsCard";
import { getCMSEntityById } from "../../services/cmsV2-api";
import AddOffersOnWA from "./AddOffersOnWA";
import Banner, { VideoBanner } from "./Banner";
import BannerCarousel from "./BannerCarousel";
import BannerHeader from "./BannerHeader";
import BannersList from "./BannersList";
import Categories from "./Categories";
import DeliveryBanner from "./DeliveryBanner";
import DeliveryTimeline from "./DeliveryTimeline";
import GroupBuy from "./GroupBuy";
import InfiniteFlatList from "./InfiniteFlatList";
import NxMBanner from "./NxMBanner";
import NxMBannerContainer from "./NxMBannerContainer";
import ProductList from "./ProductList";
import ProductSkeleton from "./Skeleton/ProductSkeleton";
import SubPage from "./SubPage";
import ListShareItem from "./ListShareItem";
import WhatsappMsgBanner from "./WhatsappMsgBanner";
import SelfOrderBanner from "./SelfOrderBanner";
import LeaderAgentContact from "./LeaderAgentContact";

import ContactSyncBanner from "./ContactSyncBanner";
import ShopShareBanner from "./ShopShareBanner";
import WebCMS from "./WebCMS";
import NonInventoryProductList from "../NonInventoryProductList";

const FetchCMSEntity = memo((props) => {
  const navigation = useNavigation();
  const route = useRoute();
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const { fromCart = false } = props;
  const routeName = route?.name ?? "";
  const { user } = useSelector((state) => ({
    user: state.auth.user,
  }));

  // CMS Real estates
  const isRoot =
    routeName === "Home" ||
    routeName === "PlaceOrderSuccess" ||
    routeName === "Gullak" ||
    routeName === "ConfirmOrder";

  const fetchEntity = useCallback(async (entityId, item_limit) => {
    try {
      const { data } = await getCMSEntityById({
        id: entityId,
        root: isRoot,
        limit: item_limit,
        user_id: user?.user_id,
      });

      setEntity(data);
      if (data.type !== "BANNER") {
        setLoading(false);
      }
    } catch (e) {
      console.log(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { id, item_limit = 10 } = props;
    fetchEntity(id, item_limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEntity]);

  return (
    <>
      {entity ? (
        <RecursiveCMSComponent
          {...{
            entity,
            ...props,
            loading,
            setLoading,
            navigation,
            isRoot,
            fromCart,
            routeName,
          }}
        />
      ) : (
        <Skeleton type={props.type} />
      )}
    </>
  );
});

const Skeleton = ({ type }) => {
  switch (type) {
    case "SKU_LIST":
      return [1, 2, 3].map((i) => <ProductSkeleton key={`${i}`} />);
    default:
      return null;
  }
};

const RecursiveCMSComponent = ({ entity, isRoot, ...rest }) => {
  const { type, allow_fullscreen = true, horizontal } = entity;
  const props = {
    ...entity,
    ...rest,
  };
  switch (type) {
    case "WEB_ENTITY":
      return <WebCMS {...props} />;
    case "LIST_SHARE_ITEM":
      return <ListShareItem {...props} />;
    case "BANNER_CAROUSEL":
      return <BannerCarousel {...props} />;
    case "SKU_LIST":
      return !isRoot ? (
        <InfiniteFlatList {...props} />
      ) : (
        <ProductList {...props} fromCart={props.fromCart} />
      );
    case "BANNER_LIST":
      return <BannersList {...props} />;
    case "BANNER":
      if (props.fromNxM) {
        return <NxMBanner {...props} />;
      } else if (
        !allow_fullscreen &&
        !props.fromBannerListHorizontal &&
        !props.fromTrainingVideoListing
      ) {
        return <VideoBanner {...props} />;
      } else if (props.fromBannerListHorizontal) {
        if (props.sub_type === "whatsapp_msg_banner") {
          return <WhatsappMsgBanner {...props} />;
        }
        return <BannerListHorizontalItem {...props} />;
      } else if (props.fromTrainingVideoListing) {
        return <VideoListingItem {...props} />;
      } else if (props.sub_type === "header_banner") {
        return <BannerHeader {...props} />;
      } else if (props.sub_type === "whatsapp_msg_banner") {
        return <WhatsappMsgBanner {...props} />;
      } else {
        return <Banner {...props} />;
      }
    case "CATEGORIES":
      return <Categories {...props} />;
    case "PAGE":
      return <SubPage {...props} />;
    case "GROUP_BUY":
      return <GroupBuy {...props} />;
    case "REFER_CARD":
      return (
        <View
          style={{
            paddingTop: 4,
            paddingHorizontal: 15,
          }}
        >
          <LeaderReferLink />
        </View>
      );
    case "ADD_OFFERS_ON_WA":
      return <AddOffersOnWA />;
    case "BANNER_LIST_HORIZONTAL":
      return <BannerListHorizontal {...props} />;
    case "NxM_GRID":
      return <NxMBannerContainer {...props} />;
    case "DELIVERY_TIMELINE":
      return <DeliveryTimeline />;
    case "DELIVERY_BANNER":
      return <DeliveryBanner {...props} />;
    case "SELF_ORDER_BANNER":
      return <SelfOrderBanner {...props} />;
    case "AGENT_CONTACT_CARD":
      return <LeaderAgentContact />;
    case "CMS_STORIES":
      return <StoryBanner cms_item_id={props.cms_item_id} />;

    case "CONTACT_SYNC_CARD":
      return <ContactSyncBanner {...props} />;
    case "SHOP_SHARE":
      return <ShopShareBanner {...props} />;
    case "CL_FILL_PROFILE_CARD":
      return <CompleteClProfile {...props} />;
    case "GENERAL_SETTINGS_CARD":
      return <SettingsCard {...props} />;
    case "CL_NON_INVENTORY_SKU_LIST":
      return <NonInventoryProductList />;
    default:
      return null;
  }
};
export default FetchCMSEntity;
